import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useCallback, useMemo, useState } from 'react';
import { Filters } from '../models/Filters';
import { useRefunds } from './useRefunds';
import { useRefundsStatusCounts } from './useRefundsStatusCounts';

export const useRefundsMain = () => {
  const { params: queryParams, updateParam } = useQueryParams({
    search: { type: 'string', default: '' },
    status: { type: 'string', default: '' },
    date_from: { type: 'string', default: '' },
    date_to: { type: 'string', default: '' },
    shop_id: { type: 'string', default: '' },
  });

  const searchTerm = queryParams.search ?? '';
  const statusFilter = (queryParams.status ?? '') as Filters['status'];
  const dateFrom = queryParams.date_from ?? '';
  const dateTo = queryParams.date_to ?? '';
  const shopIdParam = queryParams.shop_id ?? '';

  const [selectedColumns, setSelectedColumns] = useState<string[]>([
    'order_ref',
    'shop_name',
    'total',
    'delivered_at',
    'status',
  ]);

  const filters: Filters = useMemo(() => {
    const trimmed = searchTerm.trim();
    return {
      ...(trimmed && { ref: trimmed }),
      ...(statusFilter && { status: statusFilter }),
      ...(dateFrom && { date_from: dateFrom }),
      ...(dateTo && { date_to: dateTo }),
      ...(shopIdParam && { shop_id: parseInt(shopIdParam) }),
      per_page: 25,
    };
  }, [searchTerm, statusFilter, dateFrom, dateTo, shopIdParam]);

  const statusCountFilters: Filters = useMemo(() => {
    const trimmed = searchTerm.trim();
    return {
      ...(trimmed && { ref: trimmed }),
      ...(dateFrom && { date_from: dateFrom }),
      ...(dateTo && { date_to: dateTo }),
      ...(shopIdParam && { shop_id: parseInt(shopIdParam) }),
      per_page: 1,
    };
  }, [searchTerm, dateFrom, dateTo, shopIdParam]);

  const { orders, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, total } =
    useRefunds(filters);
  const { counts: statusCounts, isLoading: isLoadingStatusCounts } = useRefundsStatusCounts(statusCountFilters);

  const handleSearch = useCallback(
    (value: string) => {
      updateParam('search', value.trim());
    },
    [updateParam],
  );

  const handleStatusChange = useCallback(
    (value: string) => {
      updateParam('status', value);
    },
    [updateParam],
  );

  const handleDateRangeChange = useCallback(
    (from: string, to: string) => {
      updateParam('date_from', from);
      updateParam('date_to', to);
    },
    [updateParam],
  );

  const handleShopChange = useCallback(
    (value: string) => {
      updateParam('shop_id', value);
    },
    [updateParam],
  );

  const handleColumnsChange = useCallback((newColumns: string[]) => {
    setSelectedColumns(newColumns);
  }, []);

  const handleClearFilters = useCallback(() => {
    updateParam('search', '');
    updateParam('date_from', '');
    updateParam('date_to', '');
    updateParam('shop_id', '');
  }, [updateParam]);

  const hasActiveFilters = useMemo(
    () => !!(searchTerm.trim() || statusFilter || dateFrom || dateTo || shopIdParam),
    [searchTerm, statusFilter, dateFrom, dateTo, shopIdParam],
  );

  const ordersViewProps = useMemo(
    () => ({
      orders,
      isFetching,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      selectedColumns,
      refetch,
    }),
    [orders, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage, selectedColumns, refetch],
  );

  return {
    selectedColumns,
    orders,
    isLoading,
    isFetching,
    error,
    total,
    statusCounts,
    isLoadingStatusCounts,
    ordersViewProps,
    searchTerm,
    statusFilter,
    dateFrom,
    dateTo,
    shopIdParam,
    handleSearch,
    handleStatusChange,
    handleDateRangeChange,
    handleShopChange,
    handleColumnsChange,
    handleClearFilters,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    hasActiveFilters,
  };
};
